import {
  isAllowedWorkRequestAttachmentType,
  WORK_REQUEST_ATTACHMENT_MAX_BYTES,
  WORK_REQUEST_ATTACHMENT_MAX_FILES,
} from "@shared/work-request";
import { useState } from "react";
import { Upload, X, FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface ExistingIntakeAttachment {
  id: string;
  filename: string;
  sizeBytes?: number;
}

interface FileUploadProps {
  value: File[];
  onChange: (files: File[]) => void;
  existing?: ExistingIntakeAttachment[];
  onRemoveExisting?: (id: string) => void;
  maxFiles?: number;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function FileUpload({
  value,
  onChange,
  existing = [],
  onRemoveExisting,
  maxFiles = WORK_REQUEST_ATTACHMENT_MAX_FILES,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totalCount = existing.length + value.length;

  const addFiles = (files: File[]) => {
    const validTypeFiles = files.filter((file) =>
      isAllowedWorkRequestAttachmentType(file.type, file.name),
    );

    const oversized = validTypeFiles.filter(
      (file) => file.size > WORK_REQUEST_ATTACHMENT_MAX_BYTES,
    );
    if (oversized.length > 0) {
      setError("Each file must be under 50MB.");
      return;
    }

    setError(null);
    const remaining = Math.max(0, maxFiles - existing.length);
    const newFiles = [...value, ...validTypeFiles].slice(0, remaining);
    onChange(newFiles);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    addFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    const newFiles = value.filter((_, i) => i !== index);
    onChange(newFiles);
    setError(null);
  };

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragging ? "border-primary bg-accent" : "border-border bg-background"}
          ${totalCount >= maxFiles ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover-elevate"}
        `}
      >
        <input
          type="file"
          accept="image/*,application/pdf,video/mp4,video/webm,video/quicktime,video/x-msvideo,.mp4,.webm,.mov,.avi"
          multiple
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
          disabled={totalCount >= maxFiles}
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm font-medium mb-1">
            {totalCount >= maxFiles
              ? "Maximum files reached"
              : "Drop files here or click to browse"}
          </p>
          <p className="text-xs text-muted-foreground">
            Images, PDFs, and videos — Max {maxFiles} files, 50MB each
          </p>
        </label>
      </div>

      {(existing.length > 0 || value.length > 0) && (
        <div className="mt-4 space-y-2">
          {existing.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg"
            >
              <FileIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {file.sizeBytes
                    ? `${formatFileSize(file.sizeBytes)} · already uploaded`
                    : "Already uploaded"}
                </p>
              </div>
              {onRemoveExisting && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveExisting(file.id)}
                  className="flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {value.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg"
            >
              <FileIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeFile(index)}
                className="flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
